import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { ADSENSE_PUBLISHER_ID } from "@/lib/adsense";

// Friendly labels for preview placeholders — must mirror AdminSettings.jsx.
const SLOT_LABELS = {
  home_mid: "Home — middle",
  home_lower: "Home — lower",
  brands_mid: "Brands list — below cards",
  brand_detail: "Brand detail — before related items",
  clothing_inline: "Clothing list — inline",
  clothing_detail: "Clothing detail — before related items",
  materials_mid: "Materials Guide — between sections",
  materials_certifications_mid: "Materials certifications — below cards",
  brand_certifications_mid: "Brand certifications — below cards",
  cms_inline: "CMS pages — after content",
};

// Treat any *.preview.emergentagent.com host as the dev/preview environment.
function isPreviewEnv() {
  if (typeof window === "undefined") return false;
  return /preview\.emergentagent\.com$/i.test(window.location.hostname);
}

/**
 * AdSlot — Google AdSense placement.
 *
 * In PRODUCTION: only renders the AdSense `<ins>` when all of these are true:
 *   - master `ads_enabled` toggle is on,
 *   - publisher ID is set,
 *   - this specific slot is enabled,
 *   - and a numeric ad-unit slot_id has been provided.
 *
 * In PREVIEW: shows a labeled blue placeholder whenever ads are enabled and the
 * slot is toggled on, so you can see how each placement will look in the page
 * layout without needing AdSense approval or real slot IDs.
 */
export function AdSlot({ slotKey, className = "" }) {
  const { data: settings } = useQuery({
    queryKey: ["public-settings"],
    queryFn: async () => (await api.get("/public/settings")).data,
  });
  const clientId = ADSENSE_PUBLISHER_ID;
  const adsEnabled = settings?.ads_enabled !== false; // default true for back-compat
  const slot = settings?.ad_slots?.[slotKey];
  // Back-compat: previously ad_slots[slotKey] was a bool.
  const enabled = typeof slot === "object" ? !!slot.enabled : !!slot;
  const slotId = typeof slot === "object" ? (slot.slot_id || "") : "";
  const ready = adsEnabled && enabled && clientId && slotId;
  const preview = isPreviewEnv() && adsEnabled && enabled;

  // Once AdSense actually fills the slot, the `<ins>` gains
  // `data-ad-status="filled"`. Until then, we don't want to reserve any
  // vertical space (no margin, no min-height) — the section should be
  // invisible. We poll the attribute briefly after push; if no fill happens
  // within ~6 s we leave it collapsed (so awaiting-approval / no-fill states
  // never leave a visible empty band on the page).
  const insRef = useRef(null);
  const [adFilled, setAdFilled] = useState(false);

  useEffect(() => {
    if (!ready) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch { /* AdSense will retry on next push */ }
    let cancelled = false;
    const check = () => {
      if (cancelled) return;
      const el = insRef.current;
      if (el && el.getAttribute("data-ad-status") === "filled") {
        setAdFilled(true);
        clearInterval(timer);
      }
    };
    const timer = setInterval(check, 500);
    const stopAfter = setTimeout(() => clearInterval(timer), 6000);
    return () => {
      cancelled = true;
      clearInterval(timer);
      clearTimeout(stopAfter);
    };
  }, [ready, slotKey]);

  if (!ready && !preview) return null;

  // Preview-only placeholder so you can visually QA placement.
  if (!ready && preview) {
    return (
      <div className={`my-8 ${className}`} data-testid={`ad-slot-${slotKey}-preview`}>
        <div className="rounded-md border-2 border-dashed border-blue-400 bg-blue-50 text-blue-700 px-4 py-10 text-center">
          <div className="text-xs uppercase tracking-[0.2em] mb-1 font-semibold">Ad placeholder · preview only</div>
          <div className="text-sm">
            {SLOT_LABELS[slotKey] || slotKey}
            {slot && typeof slot === "object" && !slot.slot_id && (
              <span className="block text-[11px] opacity-70 mt-1">
                Add a numeric slot ID in Admin → Settings → AdSense to serve a real ad in production.
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${adFilled ? "my-8" : ""} ${className}`}
      data-testid={`ad-slot-${slotKey}`}
      data-ad-filled={adFilled ? "true" : "false"}
    >
      <ins
        ref={insRef}
        className="adsbygoogle block"
        style={{ display: "block" }}
        data-ad-client={clientId}
        data-ad-slot={slotId}
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
    </div>
  );
}
